import { useState } from "react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  JobFrequencyEnum,
  TJobFormData,
  isDayPatternInUse,
} from "./CreateJobAndSchedule.model";
import { DayPatternEntry, describeInvalidPatternDays } from "./day-pattern";
import {
  JobDetailFields,
  SeriesTerms,
  daysOfWeekFromPattern,
  inheritedSupervisorIds,
  seriesTemplateSchedule,
  pickJobDetailFields,
  toLoadedDayPattern,
} from "./day-pattern-save";
import { useQuery } from "@tanstack/react-query";
import { CustomerCategoryEnum, ILeadDetails } from "../../SalesLead/Lead.model";
import { requests } from "../../../../services/http-common";
import { useParams } from "react-router-dom";
import moment from "moment";

export const useJobAndScheduleForm = () => {
  // `jobId` is only on the edit route. Its presence is what puts the form in
  // edit mode: the job is loaded and the day pattern is saved on its own
  // endpoint, rather than a new job being POSTed.
  const { leadId, jobId } = useParams<{ leadId: string; jobId?: string }>();
  // `isLoading` below goes false as soon as the lead request resolves, which is
  // before the async onSuccess has finished awaiting the contract and calling
  // reset(). Anything that must not be overwritten by that reset - restoring a
  // saved draft, for one - has to wait on this flag instead.
  const [isLeadDetailsApplied, setIsLeadDetailsApplied] = useState(false);
  /**
   * The series as the job sent it, kept to tell an edit from a reload: the save
   * regenerates occurrences, so untouched terms must not be sent. `null` until
   * the job answers - distinct from a loaded series whose pattern is empty,
   * which is a job that has none.
   */
  const [loadedSeries, setLoadedSeries] = useState<SeriesTerms | null>(null);
  /**
   * The job-level fields as the job sent them. Same reason, cheaper stakes: this
   * update writes columns rather than regenerating a year of shifts, but a body
   * sent for an untouched form is still a write nobody asked for.
   */
  const [loadedJobDetail, setLoadedJobDetail] = useState<JobDetailFields | null>(null);
  const [jobLoadProblem, setJobLoadProblem] = useState<string | null>(null);
  /**
   * Whether `PUT /job/:id/series` can act on this job at all.
   *
   * It is exactly "the STORED job is a weekly recurrence": `updateDayPattern`
   * refuses anything else outright, and `dayPattern` on the series body is
   * @ArrayNotEmpty, so a monthly, yearly or non-recurring job has no
   * series-edit path whatsoever and every one of the per-occurrence fields
   * stays dead on the form.
   *
   * The STORED frequency, not the form's. Recurrence and Frequency are both
   * live controls on this route, so a monthly job can be flipped to Weekly on
   * screen - and that changes nothing the series endpoint reads, because the
   * series body carries no frequency and `PUT /job/jobDetail/:id` is not sent
   * one either. Gating the controls on what the form shows would enable four
   * fields whose save the server then refuses.
   *
   * `null` until the job answers, so nothing is enabled on a guess.
   */
  const [isSeriesEditable, setIsSeriesEditable] = useState<boolean | null>(null);

  /**
   * The per-occurrence fields are disabled on the edit route, so requiring them
   * there can only deadlock: a job whose occurrences have all been deleted
   * arrives with no `jobSchedules[0]` to seed them from, and the user would be
   * refused a save over fields the screen will not let them touch. That job -
   * a weekly series with nothing to synthesize a pattern from - is precisely
   * the one this screen exists to repair.
   */
  const isPerOccurrenceRequired = !jobId;

  const requiredWhenEditable = <T extends Yup.AnySchema>(schema: T, message: string) =>
    isPerOccurrenceRequired ? (schema.required(message) as T) : schema;

  const formSchema = Yup.object().shape({
    leadId: Yup.string(),
    name: Yup.string().required("Field is required"),
    zoneCode: requiredWhenEditable(Yup.number(), "Field is required").nullable(true),
    // The job-level Employees field is hidden once the per-day pattern is in
    // use - the pattern is then authoritative - so requiring it there would
    // block every submit on a field the user cannot see.
    employeeIds: Yup.array()
      .of(Yup.string())
      .when(["recurrence", "frequency", "dayPattern"], {
        is: (
          recurrence: boolean,
          frequency: JobFrequencyEnum,
          dayPattern: DayPatternEntry[]
        ) => isDayPatternInUse({ recurrence, frequency, dayPattern }),
        then: Yup.array().of(Yup.string()),
        otherwise: Yup.array()
          .of(Yup.string())
          .min(1, "At least one employee should be selected"),
      }),
    supervisorIds: Yup.array().of(Yup.string()),
    additionalNotes: Yup.string(),
    invoiceGeneration: Yup.string(),
    jobType: Yup.string(),
    timeZone: Yup.string(),
    paymentsTerm: Yup.string(),
    recurrence: Yup.boolean(),
    frequency: Yup.mixed()
      .oneOf([...Object.values(JobFrequencyEnum), null])
      .when("recurrence", {
        is: (recurrence: boolean) => recurrence,
        then: Yup.mixed()
          .oneOf([...Object.values(JobFrequencyEnum)])
          .required("Field is required"),
      }),
    // A null interval cannot be sent: `PUT /job/:id/series` reads an absent or
    // null `recurEvery` as "leave the stored interval alone", so clearing the
    // control and saving would report success and change nothing.
    recurEvery: Yup.number()
      .nullable(true)
      .min(1, "Recur every must be at least 1")
      .when("recurrence", {
        is: (recurrence: boolean) => recurrence,
        then: Yup.number()
          .nullable(true)
          .min(1, "Recur every must be at least 1")
          .required("Field is required"),
      }),
    daysOfWeek: Yup.array().of(Yup.number()),
    // Blocks the submit only. The message is never rendered: each problem is
    // already shown on the card that owns it, and a form-level summary would
    // repeat it away from the control the user has to fix.
    dayPattern: Yup.array().when(["recurrence", "frequency"], {
      is: (recurrence: boolean, frequency: JobFrequencyEnum) =>
        Boolean(recurrence) && frequency === JobFrequencyEnum.WEEKLY,
      then: Yup.array().test(
        "day-pattern-complete",
        "Fix the highlighted days before saving",
        (value) => describeInvalidPatternDays((value ?? []) as DayPatternEntry[]).length === 0
      ),
      otherwise: Yup.array(),
    }),
    dayOfMonth: Yup.number().nullable(true).required("Field is required"),
    monthOfYear: Yup.number().nullable(true).required("Field is required"),
    startTime: requiredWhenEditable(Yup.date(), "Field is required").nullable(true),
    lateInAlertTime: requiredWhenEditable(Yup.number(), "Field is required").nullable(true),
    endTime: requiredWhenEditable(Yup.date(), "Field is required").nullable(true),
    lateOutAlertTime: requiredWhenEditable(
      Yup.number(),
      "Field is required"
    ).nullable(true),
    recurrenceEndDate: Yup.date().nullable(true).when("recurrence", {
      is: (recurrence: boolean) => recurrence,
      then: Yup.date().required("Field is required").nullable(true),
    }),
    attachment2Path: Yup.array().of(Yup.mixed()),
    //view only
    clientTitle: Yup.mixed().oneOf([...Object.values(CustomerCategoryEnum)]),
    clientId: Yup.string().nullable(true),
    clientFirstName: Yup.string().nullable(true),
    clientLastName: Yup.string().nullable(true),
    clientCompanyName: Yup.string().nullable(true),
    clientContactName: Yup.string().nullable(true),
    clientFax: Yup.string().nullable(true),
    clientPhoneNumber: Yup.string().nullable(true),
    cientContactEmail: Yup.string().nullable(true),
    leadAddress: Yup.string().nullable(true),
    leadlaborGoalHours: Yup.number().nullable(true),
    attachment1: Yup.string().nullable(true),
    leadAdditionalNotes: Yup.array(
      Yup.object().shape({
        cacategory: Yup.string(),
        name: Yup.string(),
      })
    ),
    sowFrequencies: Yup.array().required("Please select Sow Frequencies!"),
  });

  const form = useForm<TJobFormData>({
    resolver: yupResolver(formSchema),
    shouldFocusError: false,
    defaultValues: {
      leadId: null,
      name: "",
      zoneCode: null,
      employeeIds: [],
      supervisorIds: [],
      additionalNotes: "",
      invoiceGeneration: "daily",
      jobType: "",
      paymentsTerm: "testQB",
      recurrence: false,
      dayOfMonth: 1,
      monthOfYear: 0,
      // The zone the wall-clock day-pattern times are read in. The backend
      // checks it against IANAZone.isValidZone, so it has to be a real zone
      // name - the browser's own is the only one the form can know.
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      frequency: JobFrequencyEnum.WEEKLY,
      recurEvery: 1,
      daysOfWeek: [],
      // Absent from defaultValues, the field reads back as undefined until
      // something writes to it - so getValues("dayPattern") in the chip
      // handler and the schema's `when` on it would both see undefined rather
      // than an empty pattern on the first pass.
      dayPattern: [],
      startTime: moment().toDate(),
      lateInAlertTime: null,
      endTime: moment().add(30, "minutes").toDate(),
      lateOutAlertTime: null,
      recurrenceEndDate: null,
      attachment2Path: [],
      leadlaborGoalHours: null,
      leadAdditionalNotes: [],
      sowFrequencies: [],
    },
  });

  const { reset } = form;

  // const { refetch: refetchContract } = useQuery<IContract>({
  //   queryKey: [`contract-details-${lead?.contract.id}`],
  //   enabled: false,
  // });

  const { isLoading } = useQuery<ILeadDetails>({
    queryKey: [`lead-details-${leadId}`],
    queryFn: () => {
      return requests.get(`/lead/${leadId}`);
    },
    cacheTime: 0,
    onSuccess: async (data) => {
      let contract: any = null;

      try {
        contract = await fetchContract(data.contract?.id ?? "");
      } catch {
        // The contract is supplementary - a lead with no contract id requests
        // "/contract/" and rejects. Carry on with the lead data rather than
        // leaving the form unpopulated and the applied flag stuck false.
      }

      reset((values) => ({
        ...values,
        leadId: leadId || "0",
        clientId: data.client.clientId,
        customerCategory: data.customerCategory,
        clientFirstName: data.name,
        clientLastName: data.surname || "",
        clientCompanyName: data.companyName,
        clientContactName: data.contactName,
        clientFax: data.client.fax || "",
        clientPhoneNumber: data.client.phone,
        cientContactEmail: data.client.email,
        leadAddress: data.address,
        // The lead's figure seeds a NEW job only. On the edit route the job has
        // its own, and this reset races the job query's - so leaving the key out
        // here is what stops the race deciding which number the user sees, and
        // then saves back over the job's.
        ...(jobId ? {} : { leadlaborGoalHours: data.laborGoalHours }),
        attachment1: data.pdfPath,
        jobType: contract?.contractType?.name || data.contractType?.name || data.projectType?.name || "",
        sowFrequencies: contract?.sowFrequencies?.length ? contract.sowFrequencies : data.sowFrequencies || [],
        leadAdditionalNotes: [
          {
            category: "Paper Products",
            names: contract?.paperProducts.map((product: any) => product.name),
          },
          {
            category: "Scope of works",
            // @ts-ignore
            names: contract?.scopeOfWorks.map((product: any) => product.name),
          },
          {
            category: "Supplies",
            names: contract?.supplies.map((product: any) => product.name),
          },
        ],
      }));

      // Last statement: everything the lead query owns is now in the form.
      setIsLeadDetailsApplied(true);
    },
    onError: () => {
      // No reset will happen, so nothing is waiting to overwrite the form.
      setIsLeadDetailsApplied(true);
    },
    refetchOnWindowFocus: false,
  });

  const fetchContract = (contractId: string) => {
    return requests.get(`/contract/${contractId}`);
  };

  /**
   * The existing series. `GET /job/:id` sends a `dayPattern` even for a job
   * that never stored one - it synthesizes that pattern from the job's own
   * occurrences - so a legacy job scheduled on every working day arrives here
   * with one card per day, which is what makes it editable at all.
   *
   * The pattern is seeded as the API sent it (through toLoadedDayPattern, for
   * the time precision) and never through syncPatternWithDays, which would
   * dedupe a repeated weekday the cards are meant to flag. The weekday chips
   * are then derived *from* that pattern rather than read from the job, so the
   * two cannot disagree: an empty `dayPattern` beside a populated `daysOfWeek`
   * is unreachable from a load, and keeps its one existing meaning - no
   * pattern, legacy mode - everywhere that reads it.
   */
  const jobQuery = useQuery({
    queryKey: [`job-day-pattern-${jobId}`],
    queryFn: () => requests.get(`/job/${jobId}`),
    enabled: Boolean(jobId),
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onSuccess: (job: any) => {
      const pattern = toLoadedDayPattern(job?.dayPattern);
      // The series' START, for the two job-level time fields: those are shown so
      // the series has a readable beginning, and nothing reads them once a
      // pattern expands the occurrences.
      const schedule = job?.jobSchedules?.[0];
      // The occurrence a series save would INHERIT the division and the alert
      // times from, which is not generally the same row - see
      // seriesTemplateSchedule. Seeding those three from [0] put one division on
      // screen while an untouched save kept another.
      const template = seriesTemplateSchedule(job?.jobSchedules) ?? schedule;
      // And whom the series currently names, by the latest-not-union rule the
      // server inherits by. Seeded from [0] instead, adding one supervisor would
      // send the oldest occurrence's list back and reinstate somebody who had
      // been replaced mid-series.
      const supervisorIds = inheritedSupervisorIds(job?.jobSchedules);
      const isWeeklyRecurring =
        Boolean(job?.recurrence) && job?.frequency === JobFrequencyEnum.WEEKLY;

      const recurrenceEndDate = job?.jobEndTime ? moment(job.jobEndTime).toDate() : null;

      // The baselines. Taken from the JOB rather than from the form after the
      // reset: the lead query owns its own reset() and the two race, so reading
      // them back out of form state would sometimes capture the lead's values.
      setLoadedSeries({
        dayPattern: pattern,
        recurEvery: job?.recurEvery ?? null,
        recurrenceEndDate,
        // Seeded from the SAME occurrence the form's controls are seeded from,
        // so "the user changed this" means what it says. That occurrence is not
        // necessarily the one regeneration inherits from - it takes the division
        // and the alert times off the latest future occurrence and the
        // supervisors off the latest that names any - which is precisely why
        // the save sends only the fields that moved: see
        // changedOccurrenceTerms.
        supervisorIds,
        zoneCode: template?.zoneCode ?? null,
        lateInAlertTime: template?.lateInAlertTime ?? null,
        lateOutAlertTime: template?.lateOutAlertTime ?? null,
      });
      setIsSeriesEditable(isWeeklyRecurring);
      const detail = pickJobDetailFields(job ?? {});

      setLoadedJobDetail(detail);
      // Said out loud rather than left to look like a job that simply has no
      // pattern: the synthesis only comes up empty when there is no occurrence
      // to read, and these are exactly the jobs this screen exists to fix.
      setJobLoadProblem(
        isWeeklyRecurring && pattern.length === 0
          ? "This job has no per-day schedule to load. Pick the weekdays below to build one."
          : null
      );

      reset((values) => ({
        ...values,
        name: job?.name ?? values.name,
        additionalNotes: job?.additionalNotes ?? values.additionalNotes,
        invoiceGeneration: job?.invoiceGeneration ?? values.invoiceGeneration,
        // The JOB's hours, not the lead's: `GET /job/:id` spreads the entity,
        // so it does send them, whatever the response DTO's type declares.
        leadlaborGoalHours: detail.laborGoalHours,
        jobType: job?.jobType ?? values.jobType,
        paymentsTerm: job?.paymentsTerm ?? values.paymentsTerm,
        recurrence: Boolean(job?.recurrence),
        frequency: job?.frequency ?? values.frequency,
        recurEvery: job?.recurEvery ?? values.recurEvery,
        recurrenceEndDate: recurrenceEndDate ?? values.recurrenceEndDate,
        // The zone the pattern's wall-clock times are read in. The job's own,
        // where it has one - the browser's would quietly move every shift.
        timeZone: job?.timeZone || values.timeZone,
        dayPattern: pattern,
        daysOfWeek: daysOfWeekFromPattern(pattern),
        zoneCode: template?.zoneCode ?? values.zoneCode,
        startTime: schedule?.startTime
          ? moment(schedule.startTime).toDate()
          : values.startTime,
        endTime: schedule?.endTime ? moment(schedule.endTime).toDate() : values.endTime,
        lateInAlertTime: template?.lateInAlertTime ?? values.lateInAlertTime,
        lateOutAlertTime: template?.lateOutAlertTime ?? values.lateOutAlertTime,
        // With a pattern in play the pattern is what staffs the job, and the
        // job-level field is not even rendered.
        employeeIds: pattern.length
          ? []
          : (schedule?.employees ?? []).map((employee: any) => employee.id),
        supervisorIds,
      }));
    },
    onError: (error: any) => {
      setJobLoadProblem(
        error?.response?.data?.error?.message || "Could not load this job's schedule"
      );
    },
  });

  return {
    form,
    isLeadDetailsLoading: isLoading,
    isLeadDetailsApplied,
    jobId,
    // A disabled query still reports isLoading in react-query v4, so the
    // create route would hang on the Loader without this.
    isJobLoading: Boolean(jobId) && jobQuery.isLoading,
    jobLoadProblem,
    loadedSeries,
    setLoadedSeries,
    loadedJobDetail,
    setLoadedJobDetail,
    isSeriesEditable,
  };
};
